import CreateCapabilityMenu from '../shared/CreateCapabilityMenu';
import React, { useState } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, useMediaQuery, useTheme, Box, Tooltip, IconButton, Dialog, DialogContent, TextField, ClickAwayListener, Menu, MenuItem, Avatar, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CreateOutlined as CreateIcon,
  FolderOutlined as FolderIcon,
  PeopleOutlined as PeopleIcon,
  AnalyticsOutlined as AnalyticsIcon,
  PaymentOutlined as PaymentIcon,
  PublicOutlined as PublicIcon,
  PersonOutlined as PersonIcon,
  LogoutOutlined as LogoutIcon,
  SettingsOutlined as SettingsIcon,
  InfoOutlined as HelpIcon,
  FeedbackOutlined as FeedbackIcon,
  SearchOutlined as SearchIcon,
  AutoAwesomeOutlined as AutoAwesomeIcon,
  HubOutlined as IntegrationIcon,
  ChevronRightOutlined as ChevronRightIcon,
  ExpandLessOutlined as ExpandLessIcon,
  CloseOutlined as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import FeedbackModal from '../shared/FeedbackModal';
import Logo from './Logo';
import SidebarCollapseIcon from './SidebarCollapseIcon';
import SearchAutocomplete from '../shared/SearchAutocomplete';
import { useGlobalSearch } from '../shared/hooks/useGlobalSearch';
import { isProOrEnterpriseTier } from '../../utils/subscriptionTier';
import { colors, FONT_FAMILY } from '../../designTokens';
import { FAQ_BASE_URL } from '../../constants/faqLinks';
import { getLibraryView, rememberedLibraryPath } from '../library/libraryViews';
import RecentCapabilities, { clearRecentCapabilitiesCache } from './RecentCapabilities';

const HELP_CENTER_URL = FAQ_BASE_URL;

const drawerWidth = 274;
const collapsedDrawerWidth = 64;

const NAV_ICON_SIZE = 16;

const navTypographySx = {
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: 0,
};

const headerIconButtonSx = {
  p: 0.625,
  color: colors.navChromeIcon,
  transition: 'background-color 160ms ease, color 160ms ease',
  '&:hover': {
    bgcolor: colors.navHoverBg,
    color: colors.navText,
  },
  '& .MuiSvgIcon-root': { fontSize: NAV_ICON_SIZE },
};

export const OPEN_ASSISTANT_OVERLAY_EVENT = 'promptcrew:open-assistant-overlay';
export const OPEN_BUILDER_OVERLAY_EVENT = 'promptcrew:open-builder-overlay';

const coreProductNavItems = [
  { text: 'Assistant', icon: <AutoAwesomeIcon />, path: '/assistant', overlayEvent: OPEN_ASSISTANT_OVERLAY_EVENT },
  { text: 'Build', icon: <CreateIcon />, action: 'build-menu' },
  { text: 'Library', icon: <FolderIcon />, path: '/library' },
  { text: 'Community', icon: <PublicIcon />, path: '/community' },
];

const userItems = [
  { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
  { text: 'Integrations', icon: <IntegrationIcon />, path: '/integrations' },
  { text: 'Help', icon: <HelpIcon />, externalHref: HELP_CENTER_URL },
  { text: 'Sign Out', icon: <LogoutIcon />, action: 'logout' },
];

const allAdminItems = [
  { text: 'User Management', icon: <PeopleIcon />, path: '/admin/users', roles: ['POWER_USER', 'ADMIN', 'OWNER'] },
  { text: 'Account Settings', icon: <SettingsIcon />, path: '/admin/settings', roles: ['ADMIN', 'OWNER'] },
  { text: 'Analytics & Usage', icon: <AnalyticsIcon />, path: '/admin/analytics', roles: ['POWER_USER', 'ADMIN', 'OWNER'] },
  { text: 'Billing & Licenses', icon: <PaymentIcon />, path: '/admin/billing', roles: ['ADMIN', 'OWNER'] },
];

const Navigation = ({ mobileOpen, isCollapsed, onDrawerToggle, onExpandRequest, onFeedbackSuccess, onFeedbackError }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [buildMenuAnchor, setBuildMenuAnchor] = useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const searchContainerRef = React.useRef(null);
  const { results, loading } = useGlobalSearch(searchTerm, 300);

  const isPowerUser = user?.role === 'POWER_USER';
  const isAdmin = user?.role === 'ADMIN';
  const isOwner = user?.role === 'OWNER';
  const hasAdminAccess = isAdmin || isOwner || isPowerUser;

  const adminItems = allAdminItems.filter(item => {
    if (isOwner) return item.roles.includes('OWNER');
    if (isAdmin) return item.roles.includes('ADMIN');
    if (isPowerUser) return item.roles.includes('POWER_USER');
    return false;
  }).filter((item) => {
    if (item.requiresProTier && !isProOrEnterpriseTier(user)) return false;
    return true;
  });

  const isCollapsedEffective = isMobile ? false : isCollapsed;

  const accountName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Account';
  const initials = accountName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  const accountRouteSelected = [...userItems, ...adminItems].some((item) => item.path && location.pathname.startsWith(item.path));
  React.useEffect(() => { setAccountMenuAnchor(null); }, [user?.id, location.key, isCollapsedEffective, mobileOpen]);

  const navListItemButtonBase = {
    minHeight: 34,
    py: 0.5,
    px: 1.25,
    mx: 1,
    borderRadius: 1.5,
    alignItems: 'center',
    color: colors.navText,
    transition: 'background-color 160ms ease, color 160ms ease',
    '& .MuiListItemIcon-root': {
      color: colors.navSectionLabel,
    },
    '&:hover': {
      backgroundColor: colors.navHoverBg,
      color: colors.navTextHover,
      '& .MuiListItemIcon-root': {
        color: colors.navTextHover,
      },
    },
    // Quiet selected state: neutral tint + darker text, hierarchy without color noise
    '&.Mui-selected': {
      backgroundColor: colors.navActiveBg,
      color: colors.navTextHover,
      '& .MuiListItemIcon-root': {
        color: colors.navTextHover,
      },
      '& .MuiTypography-root': {
        fontWeight: 500,
        color: colors.navTextHover,
      },
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'rgba(15, 23, 42, 0.08)',
      color: colors.navTextHover,
      '& .MuiListItemIcon-root': {
        color: colors.navTextHover,
      },
    },
    ...(isCollapsedEffective ? { mx: 0.5, px: 0.75, justifyContent: 'center' } : {}),
  };

  const navItemIconSx = {
    minWidth: isCollapsedEffective ? 40 : 24,
    width: isCollapsedEffective ? 40 : 24,
    mr: isCollapsedEffective ? 0 : 0.875,
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    color: 'inherit',
    '& .MuiSvgIcon-root': {
      fontSize: NAV_ICON_SIZE,
      display: 'block',
    },
  };

  const getNavTypographyProps = (selected = false) => ({
    sx: {
      ...navTypographySx,
      fontWeight: selected ? 500 : 400,
      color: selected ? colors.navTextHover : colors.navText,
    },
  });

  const handleItemClick = (item, event) => {
    if (item.action === 'logout') {
      clearRecentCapabilitiesCache();
      logoutUser();
    } else if (item.action === 'build-menu') {
      setBuildMenuAnchor(event.currentTarget);
    } else if (item.action === 'search') {
      setSearchModalOpen(true);
    } else if (item.externalHref) {
      window.open(item.externalHref, '_blank', 'noopener,noreferrer');
      if (isMobile) onDrawerToggle?.();
    } else if (item.overlayEvent) {
      window.dispatchEvent(new CustomEvent(item.overlayEvent));
      if (isMobile) {
        onDrawerToggle?.();
      }
    } else if (item.path) {
      navigate(item.text === 'Library' ? rememberedLibraryPath(user?.id) : item.path);
      if (isMobile) onDrawerToggle?.();
    }
  };

  const renderNavItem = (item, { listItemSx } = {}) => {
    const selected = item.text === 'Library' ? Boolean(getLibraryView(location.pathname)) : item.externalHref ? false : location.pathname === item.path;

    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.125, ...listItemSx }}>
        <Tooltip title={isCollapsedEffective ? item.text : ''} placement="right">
          <ListItemButton
            selected={selected}
            onClick={(event) => handleItemClick(item, event)}
            sx={navListItemButtonBase}
          >
            <ListItemIcon sx={navItemIconSx}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                m: 0,
                my: 0,
                py: 0,
                flex: '1 1 auto',
                minWidth: 0,
                opacity: isCollapsedEffective ? 0 : 1,
                display: isCollapsedEffective ? 'none' : 'block',
              }}
              primaryTypographyProps={getNavTypographyProps(selected)}
            />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={isMobile ? mobileOpen : true}
      onClose={onDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: isCollapsedEffective ? collapsedDrawerWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsedEffective ? collapsedDrawerWidth : drawerWidth,
          boxSizing: 'border-box',
          borderRight: `1px solid ${colors.sidebarBorder}`,
          backgroundColor: colors.sidebarSurface,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          // Divide by the app zoom (set in Container) so the fixed drawer still
          // spans the full window height; temporary drawer portals out, so fall back to 1.
          height: { xs: 'calc(100dvh / var(--app-zoom, 1))', sm: 'calc(100vh / var(--app-zoom, 1))' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsedEffective ? 'center' : 'space-between',
            px: isCollapsedEffective ? 0.75 : 2.25,
            pt: 1.875,
            pb: 1.5,
            mb: 2,
            minHeight: 58,
            flexShrink: 0,
            gap: 0.125,
          }}
        >
          {isCollapsedEffective ? (
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton
                size="small"
                onClick={() => onExpandRequest && onExpandRequest()}
                sx={headerIconButtonSx}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Logo size="nav" showIcon={false} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.125, flexShrink: 0 }}>
                <Tooltip title="Search">
                  <IconButton
                    size="small"
                    onClick={() => setSearchModalOpen(true)}
                    sx={headerIconButtonSx}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isMobile ? 'Close sidebar' : 'Toggle sidebar'}>
                  <IconButton
                    size="small"
                    onClick={onDrawerToggle}
                    sx={headerIconButtonSx}
                  >
                    {isMobile ? <CloseIcon /> : <SidebarCollapseIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>

        <List disablePadding sx={{ pt: 0, pb: 0, flexShrink: 0 }}>
          {coreProductNavItems.map((item) => renderNavItem(item))}
          {isCollapsedEffective && renderNavItem({ text: 'Search', icon: <SearchIcon />, action: 'search' })}
        </List>

        <RecentCapabilities userId={user?.id} collapsed={isCollapsedEffective} onNavigate={() => { if (isMobile) onDrawerToggle?.(); }} />
      </Box>

      <Box
        sx={{
          mt: 'auto',
          flexShrink: 0,
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          pt: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: colors.sidebarSurface,
        }}
      >
        <List disablePadding>
          <ListItem disablePadding>
            <Tooltip title="Account and settings" placement="right">
              <ListItemButton
                aria-label="Account and settings"
                aria-haspopup="menu"
                aria-expanded={Boolean(accountMenuAnchor)}
                aria-controls={accountMenuAnchor ? 'account-settings-menu' : undefined}
                selected={accountRouteSelected}
                onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
                sx={{
                  ...navListItemButtonBase,
                  justifyContent: isCollapsedEffective ? 'center' : 'flex-start',
                  minHeight: 52,
                  width: '100%',
                }}
              >
                <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: 'rgba(29, 78, 216, 0.12)', color: 'primary.main', border: '1px solid rgba(29, 78, 216, 0.22)', flexShrink: 0, mr: isCollapsedEffective ? 0 : 1.25 }}>{initials}</Avatar>
                <ListItemText
                  primary={accountName}
                  secondary={user?.email && user.email !== accountName ? user.email : undefined}
                  sx={{
                    m: 0,
                    my: 0,
                    py: 0,
                    flex: '1 1 auto',
                    minWidth: 0,
                    opacity: isCollapsedEffective ? 0 : 1,
                    display: isCollapsedEffective ? 'none' : 'block',
                  }}
                  primaryTypographyProps={{ ...getNavTypographyProps(), noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.6875rem' } }}
                />
                {!isCollapsedEffective && <ExpandLessIcon sx={{ fontSize: 18, color: colors.navSectionLabel, ml: 0.75 }} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>

      {Boolean(accountMenuAnchor) && <Menu
        id="account-settings-menu"
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={() => setAccountMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 260, maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 24px)', borderRadius: 1 } }}
        MenuListProps={{ 'aria-label': 'Account and settings' }}
      >
        {userItems.filter((item) => item.action !== 'logout').map((item) => <MenuItem key={item.text} selected={location.pathname === item.path} onClick={(event) => { setAccountMenuAnchor(null); handleItemClick(item, event); }}><ListItemIcon>{item.icon}</ListItemIcon><ListItemText>{item.text}</ListItemText></MenuItem>)}
        <MenuItem onClick={() => { setAccountMenuAnchor(null); setShowFeedbackModal(true); if (isMobile) onDrawerToggle?.(); }}><ListItemIcon><FeedbackIcon /></ListItemIcon><ListItemText>Submit Feedback</ListItemText></MenuItem>
        {hasAdminAccess && <Divider />}
        {hasAdminAccess && <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.75rem', bgcolor: 'transparent' }}>Admin</ListSubheader>}
        {adminItems.map((item) => <MenuItem key={item.text} selected={location.pathname === item.path} onClick={(event) => { setAccountMenuAnchor(null); handleItemClick(item, event); }}><ListItemIcon>{item.icon}</ListItemIcon><ListItemText>{item.text}</ListItemText></MenuItem>)}
        <Divider />
        <MenuItem onClick={(event) => { setAccountMenuAnchor(null); handleItemClick({ action: 'logout' }, event); }}><ListItemIcon><LogoutIcon /></ListItemIcon><ListItemText>Sign Out</ListItemText></MenuItem>
      </Menu>}

      <CreateCapabilityMenu anchorEl={buildMenuAnchor} onClose={() => setBuildMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onGuidedBuild={() => {
          window.dispatchEvent(new CustomEvent(OPEN_ASSISTANT_OVERLAY_EVENT, { detail: { intent: 'build' } }));
          if (isMobile) onDrawerToggle?.();
        }}
        onSelfGuidedBuild={() => {
          window.dispatchEvent(new CustomEvent(OPEN_BUILDER_OVERLAY_EVENT));
          if (isMobile) onDrawerToggle?.();
        }}
      />

      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSuccess={() => {
          if (onFeedbackSuccess) {
            onFeedbackSuccess('Thank you for your feedback! We will follow up via your registered email if more information is needed.');
          }
        }}
        onError={(errorMessage) => {
          if (onFeedbackError) {
            onFeedbackError(errorMessage || 'Failed to submit feedback. Please try again.');
          }
        }}
      />
      <Dialog
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            mt: { xs: 2, sm: 6 },
            alignSelf: 'flex-start',
            borderRadius: 2,
            overflow: 'visible',
            mx: { xs: 1.5, sm: 2 },
          }
        }}
      >
        <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2, sm: 2.5 } }}>
          <ClickAwayListener onClickAway={() => setShowAutocomplete(false)}>
            <Box ref={searchContainerRef} sx={{ position: 'relative', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="medium"
                  placeholder="Search capabilities, projects, knowledge..."
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    setShowAutocomplete(value.trim().length > 0);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && searchTerm.trim()) {
                      e.preventDefault();
                      setShowAutocomplete(true);
                    }
                  }}
                  onFocus={() => {
                    if (searchTerm.trim()) setShowAutocomplete(true);
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <IconButton onClick={() => setSearchModalOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {showAutocomplete && (
                <SearchAutocomplete
                  searchTerm={searchTerm}
                  results={results}
                  loading={loading}
                  onClose={() => setShowAutocomplete(false)}
                  showViewAll={false}
                  embedded
                />
              )}
            </Box>
          </ClickAwayListener>
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};

export default Navigation;
